/* eslint-disable react/prefer-stateless-function */
/* eslint-disable react/no-multi-comp */

import React from 'react';
import PropTypes from 'prop-types';
import BixbyKeyPlayersCard from '~/components/Bixby/BixbyKeyPlayersCard/BixbyKeyPlayersCard';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import formatKeyPlayers from '~/util/formatKeyPlayers';
import getOr from '~/util/getOr';
import normalizeBaseballEvent from '~/util/normalizeBaseballEvent';
import logEvent from '~/util/analytics';
import StyledComponent from '~/components/Styled/Styled';
import GetTheAppLinks from '~/constants/GetTheAppLinks';
import get from 'lodash/get';
import slice from 'lodash/slice';
import userLocation from '~/util/userLocation';
import moment from 'moment';
import classnames from 'classnames/bind';

import css from '~/components/Bixby/BixbyMatchup/BixbyMatchup.scss';

const cssContext = classnames.bind(css);
const isHomeFirst = league => league.sport_name === 'soccer';

const contentClick = () => {
  logEvent('click', {
    type: 'bixby_content',
    page_type: 'events',
    page_name: 'bixby_landing_page',
  });
};

const renderTeam = (
  team,
  boxScore,
  standings,
  eventStatus,
  alignment,
  ranking,
  isFirstTeam = false
) => {
  const shortRecord = get(standings, `${alignment}.short_record`, '');
  const formattedRank = get(standings, `${alignment}.formatted_rank`, '');
  const teamLogo = get(team, 'logos.small', '');
  const teamScore = get(boxScore, `score.${alignment}.score`, '');
  const teamRowClasses = cssContext(css.teamRow, {
    separator: isFirstTeam,
  });

  if (team === null || !Object.keys(team).length) {
    return (
      <div className={teamRowClasses}>
        <div className={css.teamName}>TBD</div>
      </div>
    );
  }

  return (
    <div className={teamRowClasses}>
      <div className={css.logoWrapper}>
        <img className={css.logo} src={teamLogo} alt="" />
      </div>
      <div className={css.teamDetail}>
        {ranking && <div className={css.teamName}>{`(${ranking})`}&nbsp;</div>}
        <div className={css.teamName}>{team.medium_name}</div>
        {shortRecord &&
          formattedRank && (
            <div className={css.teamRecord}>
              {shortRecord && `${shortRecord}, ${formattedRank}`}
            </div>
          )}
      </div>
      {eventStatus !== 'pre_game' && <div className={css.teamScore}>{teamScore}</div>}
    </div>
  );
};

class Matchup extends React.Component {
  constructor() {
    super();

    this.state = {
      countryCode: 'us',
    };
  }

  componentDidMount() {
    userLocation.promise.then(location => {
      this.setCountryCode(location.countryCode);
    });
  }

  setCountryCode(countryCode = 'us') {
    this.setState({ countryCode: countryCode.toLowerCase() });
  }

  tvListing() {
    const { event: { tv_listings_by_country_code } } = this.props;
    const defaultTvListing = get(tv_listings_by_country_code, 'us[0].long_name', null);

    return get(
      tv_listings_by_country_code,
      `${this.state.countryCode}[0].long_name`,
      defaultTvListing
    );
  }

  clockLabel() {
    const { event: { event_status, box_score, game_date } } = this.props;
    const gameDate = moment(game_date).format('MMM D YYYY, h:mm A');

    // eslint-disable-next-line camelcase
    if (event_status !== 'pre_game') {
      return get(box_score, 'progress.clock_label', gameDate);
    }

    return gameDate;
  }

  renderTeams() {
    const { home_team, away_team, box_score, standings, league, event_status } = this.props.event;
    const homeFirst = isHomeFirst(league);
    const homeRanking = get(this.props.event, 'top_25_rankings.home', null);
    const awayRanking = get(this.props.event, 'top_25_rankings.away', null);

    if (homeFirst) {
      return (
        <div>
          {renderTeam(home_team, box_score, standings, event_status, 'home', homeRanking, true)}
          <hr className={css.teamDivider} />
          {renderTeam(away_team, box_score, standings, event_status, 'away', awayRanking)}
          <hr className={css.teamDivider} />
        </div>
      );
    }

    return (
      <div>
        {renderTeam(away_team, box_score, standings, event_status, 'away', awayRanking, true)}
        <hr className={css.teamDivider} />
        {renderTeam(home_team, box_score, standings, event_status, 'home', homeRanking)}
        <hr className={css.teamDivider} />
      </div>
    );
  }

  renderKeyPlayers() {
    const leagueSlug = this.props.event.league.slug;

    const preGame = this.props.event.event_status === 'pre_game';

    switch (leagueSlug) {
      case 'nfl': {
        const keyPlayers = formatKeyPlayers(this.props.event);
        const title = preGame ? 'Key Players' : 'Top Performers';
        const players = [];
        const stats = [];
        if (keyPlayers) {
          Object.values(keyPlayers).forEach(position => {
            const positionPlayers = getOr(position, 'players', {});
            return positionPlayers.forEach((player, i) => {
              stats.push(position.stats[i]);
              players.push(player);
            });
          });
        }
        return (
          <BixbyKeyPlayersCard
            stats={slice(stats, 0, 2)}
            title={title}
            players={slice(players, 0, 2)}
            stars
          />
        );
      }
      case 'mlb': {
        const keyPlayers = formatKeyPlayers(normalizeBaseballEvent(this.props.event));
        return (
          <BixbyKeyPlayersCard
            stats={slice(keyPlayers.stats, 0, 2)}
            players={slice(keyPlayers.players, 0, 2)}
            title="Pitchers"
          />
        );
      }
      case 'nhl': {
        const keyPlayers = formatKeyPlayers(this.props.event);
        return (
          <BixbyKeyPlayersCard
            stats={slice(keyPlayers.stats, 0, 2)}
            players={slice(keyPlayers.players, 0, 2)}
            title="Goaltenders"
          />
        );
      }
      case 'nba': {
        const title = preGame ? 'Key Players' : 'Top Performers';
        const keyPlayers = formatKeyPlayers(this.props.event);
        return (
          <BixbyKeyPlayersCard
            stats={slice(keyPlayers.stats, 0, 2)}
            players={slice(keyPlayers.players, 0, 2)}
            title={title}
          />
        );
      }
      case 'epl': {
        const title = preGame ? 'Key Players' : 'Top Performers';
        const keyPlayers = formatKeyPlayers(this.props.event);
        return (
          <BixbyKeyPlayersCard
            stats={slice(keyPlayers.stats, 0, 2)}
            players={slice(keyPlayers.players, 0, 2)}
            title={title}
          />
        );
      }
      case 'worldcup': {
        const title = preGame ? 'Key Players' : 'Top Performers';
        const keyPlayers = formatKeyPlayers(this.props.event);
        return (
          <BixbyKeyPlayersCard
            stats={slice(keyPlayers.stats, 0, 2)}
            players={slice(keyPlayers.players, 0, 2)}
            title={title}
          />
        );
      }
      default: {
        const title = preGame ? 'Key Players' : 'Top Performers';
        const keyPlayers = formatKeyPlayers(this.props.event);
        return (
          <BixbyKeyPlayersCard
            stats={slice(keyPlayers.stats, 0, 2)}
            players={slice(keyPlayers.players, 0, 2)}
            title={title}
          />
        );
      }
    }
  }

  renderSeriesTracker() {
    if (this.props.event)
      if (this.props.event.playoff)
        return (
          <div className={css.seriesTracker}>
            <div className={css.seriesTrackerText}>{this.props.event.playoff.description}</div>
          </div>
        );
      else if (this.props.event.aggregate_result)
        return (
          <div className={css.seriesTracker}>
            <div className={css.seriesTrackerText}>
              {this.props.event.aggregate_result.description}
            </div>
          </div>
        );
    return null;
  }
  render() {
    const contentBranchLinks = GetTheAppLinks.bixby_events_content_link;
    return (
      <div className={css.container}>
        <a href={contentBranchLinks} onClick={contentClick}>
          <div className={css.blockLinks} />
        </a>
        {this.props.event && (
          <div>
            <div className={css.matchup}>
              <div className={css.heading}>
                <span className="clockLabel">{this.clockLabel()}</span>
              </div>
              {this.renderSeriesTracker(this.props.event)}
              {this.renderTeams()}
            </div>
            <div className={css.keyPlayersContainer}>{this.renderKeyPlayers()}</div>
          </div>
        )}
        {!this.props.event && (
          <div className={css.loadingSpinnerContainer}>
            <LoadingSpinner SpinnerType="circle_pulse" />
          </div>
        )}
      </div>
    );
  }
}

Matchup.propTypes = {
  event: PropTypes.shape({
    box_score: PropTypes.object,
    league: PropTypes.object.isRequired,
    standings: PropTypes.object.isRequired,
    away_team: PropTypes.object.isRequired,
    home_team: PropTypes.object.isRequired,
    tv_listings_by_country_code: PropTypes.object.isRequired,
    game_date: PropTypes.string.isRequired,
    event_status: PropTypes.string.isRequired,
    playoff: PropTypes.object,
    aggregate_result: PropTypes.object,
    top_25_rankings: PropTypes.object,
  }).isRequired,
};

Matchup.displayName = 'Matchup';

export default StyledComponent(Matchup, [css]);
